import { getConfig } from '@/config/config';
import type { SGWTConnectCore } from '@sgwt/connect-core';
import type { ISGWTConnectIdTokenClaims } from '@sgwt/connect-core/dist/src/SGWTConnectIdTokenClaims';
import type { ISGWTConnectOpenIDStandardClaims } from '@sgwt/connect-core/dist/src/SGWTConnectOpenIDUserInfo';
import type { SgwtConnectHTMLElement } from '@sgwt/sgwt-widgets-react';
import type { SgWidgetsBus } from './config/app.config';

const appConfig = getConfig();

export const useFake = appConfig?.env ? appConfig.env === 'local' || appConfig.env === 'devci' || appConfig.env === 'deve2e' || appConfig.env === 'demo' : true;

// https://developer.mozilla.org/fr/docs/Web/API/window/location
export function getUrlParam(name: string): string | undefined {
  return unescape(window.location.search.replace(new RegExp(`^(?:.*[&\\?]${escape(name).replace(/[\.\+\*]/g, '\\$&')}(?:\\=([^&]*))?)?.*$`, 'i'), '$1')) || undefined;
}

const getNameFromEmail = (email: string) => email.split('@')[0].replace('-ext', '').replace('.', ' ');

function getFakeUserFromUrl() {
  return getUrlParam('user');
}

export const getFakeUser = () => getFakeUserFromUrl() || appConfig.fake_user;

export const setupFakeSGConnect = (originalSgConnect: SGWTConnectCore) => {
  // const originalSgConnect = setupSGWTConnect(appConfig.sgconnect);
  const fakeUserEmail = getFakeUser()!;

  originalSgConnect.getIdTokenClaims = () =>
    ({
      sub: fakeUserEmail,
    }) as ISGWTConnectIdTokenClaims;

  const originalFetchUserInfo = originalSgConnect.fetchUserInfo.bind(originalSgConnect);
  originalSgConnect.fetchUserInfo = (callback) => {
    originalFetchUserInfo((error, claims) => {
      const fakeUser = {
        ...claims,
        sub: fakeUserEmail,
        mail: fakeUserEmail,
        name: getNameFromEmail(fakeUserEmail),
      } as ISGWTConnectOpenIDStandardClaims;
      callback(error, fakeUser);
    });
    return originalSgConnect;
  };

  return originalSgConnect;
};

export const getSgwtConnect = () => (document.querySelector('sgwt-connect') as SgwtConnectHTMLElement)?.sgwtConnect; // | undefined; // useFake ? setupFakeSGConnect() : setupSGWTConnect(appConfig.sgconnect);

export const BUS_TOPIC_SGCONNECT_USERINFO = 'sg-connect.user-info';
export const TOPIC_SGCONNECT_READY = 'sgwt-connect--ready';

export function getWidgetBus(): SgWidgetsBus | null {
  const widgetConfiguration = window.SGWTWidgetConfiguration;
  return widgetConfiguration?.bus;
}

export function getUserInfoData(): { origin_network: string } | undefined {
  const bus = getWidgetBus();

  return bus?.dangerouslyGetCurrentValue<{ origin_network: string }>(BUS_TOPIC_SGCONNECT_USERINFO);
}

export const getIsUserFromLan = (origin_network: string | undefined) => origin_network === 'LAN';

export function getAppropriateApiUrl(apiUrl: string, apiUrlForLan: string) {
  const userInfo = getUserInfoData()

  const isUserFromLan = getIsUserFromLan(userInfo?.origin_network);

  return isUserFromLan ? apiUrlForLan : apiUrl

}


export interface SgConnectUserInfo {
  name: string;
  mail: string;
  sesame_id: string;
  origin_network: string;
}

export interface SgwtWidgetBusSubscribers {
  onUserInfo?: (userinfo: SgConnectUserInfo | undefined) => void;
}


const subscribeToSGWTWidgetBus = ({ onUserInfo }: SgwtWidgetBusSubscribers): void => {
  if (onUserInfo) {
    window.SGWTWidgetConfiguration.bus.subscribe(BUS_TOPIC_SGCONNECT_USERINFO, onUserInfo);
  }
};

export const setupSgwtConnectWidget = (
  subscribers: SgwtWidgetBusSubscribers,
  sgwtConnectInstance = getSgwtConnect(),
): void => {
  const widget = document.querySelector<SgwtConnectHTMLElement>('sgwt-connect');
  if (widget) {
    // When the code is executed, the widget may not have been initialized. So, we need to check that
    if (typeof widget.setSgwtConnectInstance === 'undefined') {
      // Widget is not initialized yet, so we will wait the event that indicates the widget is ready...
      const handleSgwtConnectReady = (): void => {
        widget.setSgwtConnectInstance(sgwtConnectInstance);
        subscribeToSGWTWidgetBus(subscribers);
        // Remove the event listener
        widget.removeEventListener(TOPIC_SGCONNECT_READY, handleSgwtConnectReady);
      };
      widget.addEventListener(TOPIC_SGCONNECT_READY, handleSgwtConnectReady);
    } else {
      // Widget is initialized...
      widget.setSgwtConnectInstance(sgwtConnectInstance);
      subscribeToSGWTWidgetBus(subscribers);
    }
  }
};
